import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PortalShell } from "@/components/portal/PortalShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app/crm", label: "Übersicht" },
  { to: "/app/crm/customers", label: "Kunden" },
  { to: "/app/crm/leads", label: "Leads" },
  { to: "/app/crm/projects", label: "Projekte" },
];

export const Route = createFileRoute("/_authenticated/app/crm/customers/$id")({
  ssr: false,
  head: () => ({ meta: [{ title: "Kundenakte" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: CustomerDetail,
});

function CustomerDetail() {
  const { id } = Route.useParams();

  const { data: customer } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("*").eq("id", id).single();
      if (error) throw error;
      return data;
    },
  });

  const { data: projects } = useQuery({
    queryKey: ["customer-projects", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, project_no, name, status, created_at")
        .eq("customer_id", id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  if (!customer) return <PortalShell nav={NAV}><p>Lade …</p></PortalShell>;

  const display = customer.company || `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim() || "—";

  return (
    <PortalShell nav={NAV}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{display}</h1>
          <p className="text-sm text-muted-foreground font-mono">{customer.customer_no}</p>
        </div>
        <Link to="/app/crm/customers"><Button variant="outline" size="sm">Zurück</Button></Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Stammdaten</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Typ:</span> {customer.type === "business" ? "Geschäftlich" : "Privat"}</div>
            <div><span className="text-muted-foreground">E-Mail:</span> {customer.email ?? "—"}</div>
            <div><span className="text-muted-foreground">Telefon:</span> {customer.phone ?? "—"}</div>
            <div><span className="text-muted-foreground">Adresse:</span> {[customer.street, customer.house_no].filter(Boolean).join(" ") || "—"}, {[customer.postal_code, customer.city].filter(Boolean).join(" ")}</div>
            <div><span className="text-muted-foreground">Status:</span> {customer.status}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Projekte</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Nr.</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {(projects ?? []).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/app/crm/projects/$id" params={{ id: p.id }} className="underline">{p.project_no}</Link>
                    </TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.status}</TableCell>
                  </TableRow>
                ))}
                {(projects ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">Noch keine Projekte.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </PortalShell>
  );
}
